import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import {
    BankReconciliationExceptionAgingRow,
    BankReconciliationOutstandingItemRow,
    BankReconciliationPagedReportResponse,
    BankReconciliationReportFilter,
    BankReconciliationUnclearedBookRow,
    BankReconciliationUnmatchedBankLineRow,
    BankReconciliationVarianceRow
} from '../models/bank-reconciliation.model';

type ReportType = 'unmatched-bank-lines' | 'uncleared-book-transactions' | 'outstanding-items' | 'variance' | 'exception-aging';
type ReportRow = BankReconciliationUnmatchedBankLineRow | BankReconciliationUnclearedBookRow | BankReconciliationOutstandingItemRow | BankReconciliationVarianceRow | BankReconciliationExceptionAgingRow;

@Component({
    selector: 'app-bank-reconciliation-reports',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TagModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Bank Reconciliation" [title]="title" [subtitle]="subtitle">
                <div actions>
                    <button pButton type="button" icon="pi pi-chart-line" label="Dashboard" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/dashboard'])"></button>
                    <button pButton type="button" icon="pi pi-refresh" label="Refresh" [loading]="loading" *ngIf="reportType" (click)="load()"></button>
                </div>
            </app-page-hero>

            <ng-container *ngIf="!reportType; else reportView">
                <div class="report-grid">
                    <button pButton type="button" icon="pi pi-exclamation-circle" label="Unmatched Bank Lines" (click)="open('unmatched-bank-lines')"></button>
                    <button pButton type="button" icon="pi pi-book" label="Uncleared Book Transactions" [outlined]="true" (click)="open('uncleared-book-transactions')"></button>
                    <button pButton type="button" icon="pi pi-wallet" label="Outstanding Deposits/Payments" [outlined]="true" (click)="open('outstanding-items')"></button>
                    <button pButton type="button" icon="pi pi-sliders-h" label="Variance" [outlined]="true" (click)="open('variance')"></button>
                    <button pButton type="button" icon="pi pi-clock" label="Exception Aging" [outlined]="true" (click)="open('exception-aging')"></button>
                </div>
            </ng-container>

            <ng-template #reportView>
                <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

                <app-page-data-panel [title]="title" [resultText]="totalRecords + ' rows'">
                    <div actions class="toolbar">
                        <input pInputText placeholder="Search" [(ngModel)]="searchText" (keyup.enter)="load()">
                        <p-inputNumber [(ngModel)]="bankAccountId" placeholder="Bank account" [useGrouping]="false"></p-inputNumber>
                        <p-inputNumber [(ngModel)]="sessionId" placeholder="Session" [useGrouping]="false"></p-inputNumber>
                        <p-inputNumber [(ngModel)]="statementId" placeholder="Statement" [useGrouping]="false"></p-inputNumber>
                        <p-select [options]="statusOptions" [(ngModel)]="status" placeholder="Status" [showClear]="true"></p-select>
                        <p-datePicker [(ngModel)]="dateRange" selectionMode="range" dateFormat="yy-mm-dd" placeholder="Date range"></p-datePicker>
                        <button pButton type="button" icon="pi pi-search" label="Search" [outlined]="true" (click)="load()"></button>
                    </div>

                    <p-table [value]="items" [loading]="loading" [lazy]="true" [paginator]="true" [rows]="rowsPerPage" [totalRecords]="totalRecords"
                             styleClass="p-datatable-sm" (onLazyLoad)="onLazyLoad($event)">
                        <ng-container [ngSwitch]="reportType">
                            <ng-container *ngSwitchCase="'unmatched-bank-lines'">
                                <ng-template pTemplate="header"><tr><th>Bank Line</th><th>Bank Account</th><th>Date</th><th>Counterparty</th><th>Open Amount</th><th>Status</th><th></th></tr></ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td><strong>{{ row.statementNumber || ('Statement #' + row.statementId) }}</strong><div class="muted">Line {{ row.lineNumber }} | {{ row.reference || '-' }}</div></td>
                                        <td>{{ row.bankAccountName || ('Bank #' + row.bankAccountId) }}</td>
                                        <td>{{ row.transactionDate || '-' }}</td>
                                        <td>{{ row.counterparty || '-' }}</td>
                                        <td>{{ amount(row.openAmount) }} {{ row.currencyCode || '' }}</td>
                                        <td><p-tag [value]="row.status" [severity]="lineSeverity(row.status)"></p-tag></td>
                                        <td class="actions"><button pButton type="button" icon="pi pi-external-link" [text]="true" (click)="openLine(row)"></button></td>
                                    </tr>
                                </ng-template>
                            </ng-container>

                            <ng-container *ngSwitchCase="'uncleared-book-transactions'">
                                <ng-template pTemplate="header"><tr><th>Book Transaction</th><th>Bank Account</th><th>Date</th><th>Source</th><th>Open Amount</th><th>Partner</th></tr></ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td><strong>{{ row.entryNumber || ('Journal #' + row.journalEntryId) }}</strong><div class="muted">{{ row.name || row.reference || '-' }}</div></td>
                                        <td>{{ row.bankAccountName || ('Bank #' + row.bankAccountId) }}</td>
                                        <td>{{ row.entryDate || '-' }}</td>
                                        <td><p-tag [value]="row.sourceType || 'JOURNAL_ITEM'" severity="info"></p-tag><div class="muted">{{ row.sourceNumber || '-' }}</div></td>
                                        <td>{{ amount(row.openAmount) }} {{ row.currencyCode || '' }}</td>
                                        <td>{{ row.partnerType || '-' }} #{{ row.partnerId || '-' }}</td>
                                    </tr>
                                </ng-template>
                            </ng-container>

                            <ng-container *ngSwitchCase="'outstanding-items'">
                                <ng-template pTemplate="header"><tr><th>Outstanding Item</th><th>Type</th><th>Bank Account</th><th>Date</th><th>Open Amount</th><th>Source</th></tr></ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td><strong>{{ row.entryNumber || ('Journal #' + row.journalEntryId) }}</strong><div class="muted">{{ row.name || row.reference || '-' }}</div></td>
                                        <td><p-tag [value]="row.outstandingType" [severity]="row.outstandingType === 'DEPOSIT' ? 'success' : 'warn'"></p-tag></td>
                                        <td>{{ row.bankAccountName || ('Bank #' + row.bankAccountId) }}</td>
                                        <td>{{ row.entryDate || '-' }}</td>
                                        <td>{{ amount(row.openAmount) }} {{ row.currencyCode || '' }}</td>
                                        <td>{{ row.sourceType || 'JOURNAL_ITEM' }} {{ row.sourceNumber || '' }}</td>
                                    </tr>
                                </ng-template>
                            </ng-container>

                            <ng-container *ngSwitchCase="'variance'">
                                <ng-template pTemplate="header"><tr><th>Session</th><th>Bank Account</th><th>Period</th><th>Variance</th><th>Tolerance</th><th>Blocking</th><th></th></tr></ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td><strong>{{ row.statementNumber || ('Session #' + row.sessionId) }}</strong><div class="muted">{{ row.status }}</div></td>
                                        <td>{{ row.bankAccountName || ('Bank #' + row.bankAccountId) }}</td>
                                        <td>{{ row.periodStartDate || '-' }} to {{ row.periodEndDate || '-' }}</td>
                                        <td [class.negative]="row.outsideTolerance">{{ amount(row.variance) }}</td>
                                        <td>{{ amount(row.varianceTolerance) }}</td>
                                        <td>{{ row.blockedReason || 'Clear' }}</td>
                                        <td class="actions"><button pButton type="button" icon="pi pi-external-link" [text]="true" (click)="openSession(row.sessionId)"></button></td>
                                    </tr>
                                </ng-template>
                            </ng-container>

                            <ng-container *ngSwitchCase="'exception-aging'">
                                <ng-template pTemplate="header"><tr><th>Exception</th><th>Bank Account</th><th>Age</th><th>Severity</th><th>Status</th><th></th></tr></ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td><strong>{{ row.exceptionType }}</strong><div class="muted">{{ row.description || '-' }}</div></td>
                                        <td>{{ row.bankAccountName || ('Bank #' + row.bankAccountId) }}</td>
                                        <td>{{ row.agingBucket }} ({{ row.ageDays }}d)</td>
                                        <td>{{ row.severity }}</td>
                                        <td><p-tag [value]="row.status" [severity]="exceptionSeverity(row.status)"></p-tag></td>
                                        <td class="actions"><button pButton type="button" icon="pi pi-external-link" [text]="true" *ngIf="row.sessionId" (click)="openSession(row.sessionId)"></button></td>
                                    </tr>
                                </ng-template>
                            </ng-container>
                        </ng-container>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="7">No rows match the selected filters.</td></tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>
            </ng-template>
        </app-page-shell>
    `,
    styles: [`
        .report-grid { display: grid; grid-template-columns: repeat(3, minmax(220px, 1fr)); gap: .75rem; }
        .toolbar { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; }
        .toolbar input { min-width: 220px; }
        .toolbar p-inputNumber, .toolbar p-select { min-width: 160px; }
        .muted { color: #64748b; font-size: .78rem; margin-top: .2rem; }
        .actions { text-align: right; }
        .negative { color: #b91c1c; font-weight: 700; }
        @media (max-width: 900px) { .report-grid { grid-template-columns: 1fr; } }
    `]
})
export class BankReconciliationReportsComponent implements OnInit {
    reportType?: ReportType;
    items: any[] = [];
    totalRecords = 0;
    rowsPerPage = 50;
    page = 0;
    loading = false;
    metrics: PageMetric[] = [];
    searchText = '';
    bankAccountId?: number;
    sessionId?: number;
    statementId?: number;
    status = '';
    dateRange?: Date[];

    constructor(
        private route: ActivatedRoute,
        private service: BankReconciliationService,
        private sessionStorageService: SessionStorageService,
        public router: Router
    ) {}

    get title(): string {
        if (!this.reportType) return 'Reports';
        return this.reportMeta[this.reportType].title;
    }

    get subtitle(): string {
        if (!this.reportType) return 'Choose a reconciliation report for bank lines, book transactions, outstanding items, variance, or exception aging.';
        return this.reportMeta[this.reportType].subtitle;
    }

    get statusOptions(): string[] {
        if (this.reportType === 'exception-aging') return ['OPEN', 'IN_REVIEW', 'RESOLVED', 'EXCLUDED'];
        if (this.reportType === 'variance') return ['DRAFT', 'IN_PROGRESS', 'READY_FOR_REVIEW', 'APPROVED', 'CLOSED', 'REOPENED', 'CANCELLED'];
        if (this.reportType === 'unmatched-bank-lines') return ['UNMATCHED', 'PARTIAL', 'EXCEPTION'];
        return [];
    }

    private readonly reportMeta: Record<ReportType, { title: string; subtitle: string }> = {
        'unmatched-bank-lines': { title: 'Unmatched Bank Lines', subtitle: 'Bank-side transactions that remain unmatched, partial, or in exception.' },
        'uncleared-book-transactions': { title: 'Uncleared Book Transactions', subtitle: 'Open bank-ledger journal items that are available for reconciliation.' },
        'outstanding-items': { title: 'Outstanding Deposits/Payments', subtitle: 'Open book transactions classified as outstanding deposits or payments.' },
        'variance': { title: 'Variance Report', subtitle: 'Session variances, tolerances, unresolved lines, and open exception blockers.' },
        'exception-aging': { title: 'Exception Aging', subtitle: 'Open and in-review reconciliation exceptions by age bucket.' }
    };

    ngOnInit(): void {
        this.reportType = this.route.snapshot.data['reportType'];
        const params = this.route.snapshot.queryParamMap;
        this.bankAccountId = Number(params.get('bankAccountId')) || undefined;
        this.sessionId = Number(params.get('sessionId')) || undefined;
        this.statementId = Number(params.get('statementId')) || undefined;
        if (this.reportType) this.load();
    }

    open(type: ReportType): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/reports', type]);
    }

    onLazyLoad(event: TableLazyLoadEvent): void {
        this.page = Math.floor((event.first ?? 0) / (event.rows ?? this.rowsPerPage));
        this.rowsPerPage = event.rows ?? this.rowsPerPage;
        this.load();
    }

    load(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId || !this.reportType) return;
        this.loading = true;
        const filter: BankReconciliationReportFilter = {
            bankAccountId: this.bankAccountId,
            sessionId: this.sessionId,
            statementId: this.statementId,
            status: this.status,
            searchText: this.searchText,
            dateFrom: this.toDate(this.dateRange?.[0]),
            dateTo: this.toDate(this.dateRange?.[1]),
            page: this.page,
            size: this.rowsPerPage
        };
        this.request(companyId, filter).subscribe({
            next: response => this.applyResponse(response),
            error: () => this.loading = false
        });
    }

    openLine(row: BankReconciliationUnmatchedBankLineRow): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/statements', row.statementId]);
    }

    openSession(sessionId: number): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', sessionId]);
    }

    amount(value?: number): string {
        return (value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    lineSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'MATCHED') return 'success';
        if (status === 'EXCEPTION') return 'danger';
        if (status === 'PARTIAL') return 'info';
        return 'warn';
    }

    exceptionSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'RESOLVED') return 'success';
        if (status === 'EXCLUDED') return 'danger';
        if (status === 'IN_REVIEW') return 'info';
        return 'warn';
    }

    private request(companyId: number, filter: BankReconciliationReportFilter): Observable<BankReconciliationPagedReportResponse<ReportRow>> {
        switch (this.reportType) {
            case 'unmatched-bank-lines':
                return this.service.getUnmatchedBankLinesReport(companyId, filter) as Observable<BankReconciliationPagedReportResponse<ReportRow>>;
            case 'uncleared-book-transactions':
                return this.service.getUnclearedBookTransactionsReport(companyId, filter) as Observable<BankReconciliationPagedReportResponse<ReportRow>>;
            case 'outstanding-items':
                return this.service.getOutstandingItemsReport(companyId, filter) as Observable<BankReconciliationPagedReportResponse<ReportRow>>;
            case 'variance':
                return this.service.getVarianceReport(companyId, filter) as Observable<BankReconciliationPagedReportResponse<ReportRow>>;
            case 'exception-aging':
                return this.service.getExceptionAgingReport(companyId, filter) as Observable<BankReconciliationPagedReportResponse<ReportRow>>;
            default:
                return this.service.getUnmatchedBankLinesReport(companyId, filter) as Observable<BankReconciliationPagedReportResponse<ReportRow>>;
        }
    }

    private applyResponse(response: BankReconciliationPagedReportResponse<ReportRow>): void {
        this.items = response.items ?? [];
        this.totalRecords = response.count ?? 0;
        this.metrics = [
            { label: 'Rows', value: response.count ?? 0, tone: 'info' },
            { label: 'Total amount', value: this.amount(response.totalAmount), tone: response.totalAmount ? 'warn' : 'success' },
            { label: 'Debit/open deposits', value: this.amount(response.debitTotal), tone: 'success' },
            { label: 'Credit/open payments', value: this.amount(response.creditTotal), tone: 'warn' }
        ];
        this.loading = false;
    }

    private toDate(value?: Date): string | undefined {
        return value ? value.toISOString().slice(0, 10) : undefined;
    }
}
